import React, { Component } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { pdfjs } from "react-pdf";
import ResponsivePdfViewer from "./ResponsivePdfViewer";
import { fetchResumeFile } from "api/menu/hr/resume/actions";
import settings from "utils/settings";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const { apiUrl } = settings;

class ResumeViewerModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pdfData: null,
            loading: true,
            error: null,
            pdfControls: null
        };
    }

    componentDidMount() {
        this.loadResumeFile();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.fileId !== this.props.fileId && this.props.fileId) {
            this.loadResumeFile();
        }
    }

    getFileExtension = (filename) => {
        return filename ? filename.split('.').pop().toLowerCase() : '';
    };

    isPdfFile = (filename) => {
        if (!filename) {
            // If no filename, assume PDF (most common case)
            return true;
        }
        const extension = this.getFileExtension(filename);
        return extension === 'pdf';
    };

    loadResumeFile = () => {
        const { fileId, filename } = this.props;
        
        if (!fileId) return;



        // If it's not a PDF, open in browser instead of loading in modal
        if (!this.isPdfFile(filename)) {
            this.openInBrowser();
            return;
        }

        // Load PDF data for in-app viewing
        this.setState({ loading: true, error: null, pdfControls: null });
        
        this.props.fetchResumeFile(fileId, (data) => {
            if (data) {
                this.setState({ 
                    pdfData: data, 
                    loading: false 
                });
            } else {
                this.setState({ 
                    error: "Failed to load resume file",
                    loading: false 
                });
            }
        });
    };

    openInBrowser = () => {
        const { fileId, filename } = this.props;
        
        // For non-PDF files, we need to download the file first (since API requires auth)
        // Then create a blob URL to open it in browser
        this.props.fetchResumeFile(fileId, (data) => {
            if (data) {
                // Create blob URL and open in new tab
                const file = new Blob([data], {
                    type: this.getFileExtension(filename) === 'docx' ? 
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
                        'application/msword'
                });
                const fileUrl = URL.createObjectURL(file);
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
                
                // Clean up the URL after a delay
                setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
                
                toast(`Opening ${filename} in new tab`, { type: toast.TYPE.INFO });
            } else {
                toast(`Failed to open ${filename}`, { type: toast.TYPE.ERROR });
            }
        });
        
        // Close modal since we're opening in browser
        this.props.onHide();
    };

    handleControlsReady = (controls) => {
        // Always update controls to reflect current state
        this.setState({ pdfControls: controls });
    };

    render() {
        const { show, onHide, filename } = this.props;
        const { pdfData, loading, error, pdfControls } = this.state;

        // Don't render modal for non-PDF files (they open in browser)
        if (!this.isPdfFile(filename)) {
            return null;
        }

        return (
            <>
                <style>
                    {`
                        .resume-modal-95 {
                            max-width: 95vw !important;
                            width: 95vw !important;
                        }
                        .resume-modal-95 .modal-content {
                            height: 90vh !important;
                            max-height: 90vh !important;
                        }
                        .resume-modal-95 .modal-body {
                            height: calc(90vh - 120px) !important;
                            overflow-y: auto !important;
                            background-color: #f8f9fa !important;
                        }
                    `}
                </style>
                <Modal 
                    show={show} 
                    onHide={onHide} 
                    centered
                    dialogClassName="resume-modal-95"
                >
                    <Modal.Header closeButton className="bg-light">
                        <Modal.Title>Resume: {filename || "Unknown file"}</Modal.Title>
                        {pdfControls && (
                            <div className="d-flex align-items-center ml-auto mr-3">
                                {pdfControls.pagerComponent}
                                <div className="ml-3">
                                    {pdfControls.exportButton}
                                </div>
                            </div>
                        )}
                    </Modal.Header>
                <Modal.Body className="p-0">
                    {loading && (
                        <div className="text-center p-4">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <div className="mt-2">Loading resume...</div>
                        </div>
                    )}
                    
                    {error && (
                        <div className="alert alert-danger m-3">
                            <strong>Error:</strong> {error}
                        </div>
                    )}
                    
                    {pdfData && !loading && !error && (
                        <div style={{ 
                            backgroundColor: '#ffffff',
                            padding: '20px',
                            minHeight: '100%',
                            maxWidth: '100%',
                            overflow: 'auto'
                        }}>
                            <ResponsivePdfViewer 
                                pdfData={pdfData} 
                                name={filename || "resume.pdf"}
                                onControlsReady={this.handleControlsReady}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
        );
    }
}

ResumeViewerModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    fileId: PropTypes.number,
    filename: PropTypes.string,
    fetchResumeFile: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => ({
    fetchResumeFile: bindActionCreators(fetchResumeFile, dispatch)
});

export default connect(null, mapDispatchToProps)(ResumeViewerModal);
